<template>
	<Row class="shared-user-container width-100 flex-align-center flex-justify-space-between">
		<Row class="flex-align-center">
			<ShareFeatureItemAvatarAndName :name="userFullName" :item="user" />
			<h5 class="user-email-text show-on-hover reg ml-3">{{ userEmail }}</h5>
		</Row>
		<Row class="flex-align-center">
			<h5 v-if="showRemoveAccess" @click="removeAccess" class="link show-on-hover reg mr-2">Remove access</h5>
			<h5 v-if="isSetupRequired" class="setup-required-text reg">Setup required</h5>
			<template v-if="!hideVisibilityStatus">
				<h3 class="visibility-state-text show-on-hover reg ml-4">{{ visibilityStatus }}</h3>
				<FeatureVisibilityDropdown
					class="visibility-dropdown ml-5"
					:isLocked="isLocked"
					:isVisible="isVisible"
					@set-hidden="setHidden"
					@set-visible="setVisible"
					@remove-access="removeAccess"
					:disabled="isLocked && disabled"
					:isLoading="loadingVisibilityChange"
					:allowRemoveAccess="allowRemoveAccess"
					@set-visible-locked="setVisibleLocked"
					:allowLockVisibility="allowLockVisibility"
					:disabledExplanation="disabledExplanation"
				/>
			</template>
		</Row>
	</Row>
</template>

<script>
import { AccountPermissions } from '@ply-io/ply-front-shared-utilities';

import FeatureVisibilityDropdown from '@/components/FeatureVisibility/FeatureVisibilityDropdown.vue';
import AppAvatarService from '@/components/common/AppAvatar/app-avatar-service.js';
import ShareFeatureItemAvatarAndName from '@/components/dialogs/ShareFeatureDialog/ShareFeatureItemAvatarAndName.vue';
import { OPTIONS_MAP } from '@/components/dialogs/ShareModuleDialog/share-module-dialog.const.js';
import { hasAccountPermission } from '@/store/modules/account/getters';
import { user } from '@/store/modules/auth/getters';
import { updateWorkflowUserRole, toggleWorkflowIsOnSettings } from '@/store/modules/workflows/actions';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';

export default {
	name: 'SharedUserItem',
	components: {
		FeatureVisibilityDropdown,
		ShareFeatureItemAvatarAndName,
	},
	props: {
		user: { type: Object },
		workflow: { type: Object },
		disabled: { type: Boolean },
		insideGroup: { type: Boolean },
		userShareData: { type: Object },
		disabledExplanation: { type: String },
		hideVisibilityStatus: { type: Boolean },
	},

	computed: {
		...mapGettersShorthand({ me: user, getWorkflow, hasAccountPermission }),
		isSetupRequired() {
			return this.userShareData.is_setup_required;
		},
		allowRemoveAccess() {
			if (this.insideGroup) {
				return false;
			}
			return !this.isWorkflowOwnerOrMe;
		},
		isWorkflowOwnerOrMe() {
			return this.isWorkflowOwner || this.userIsMe;
		},
		showRemoveAccess() {
			return this.allowRemoveAccess && this.hideVisibilityStatus;
		},
		allowLockVisibility() {
			if (this.insideGroup) {
				return false;
			}
			return this.canLockVisibilityForOthers && !this.isWorkflowOwnerOrMe;
		},
		workflowId() {
			return this.workflow.id;
		},
		canLockVisibilityForOthers() {
			return this.hasAccountPermission(AccountPermissions.EnforceCanUseAbility);
		},
		visibilityStatus() {
			if (this.isLocked) {
				return 'Visible - locked';
			}
			if (this.isVisible) {
				return 'Visible';
			}
			return 'Hidden';
		},
		userIsMe() {
			return this.me.id === this.userId;
		},
		userId() {
			return this.user.user_id || this.user.id;
		},
		isLocked: {
			get() {
				if (!this.isVisible) {
					return false;
				}
				return this.userShareData.role === OPTIONS_MAP.CAN_USE_ENFORCED;
			},
			set(newV) {
				if (this.insideGroup) {
					// DO NOT CHANGE ROLE FOR USERS INSIDE GROUP
					return;
				}
				if (this.allowLockVisibility) {
					const role = newV ? OPTIONS_MAP.CAN_USE_ENFORCED : OPTIONS_MAP.CAN_USE;
					this.updateUserVisibility([this.user?.email], role);
				}
			},
		},
		isVisible: {
			get() {
				return this.userShareData.is_on;
			},
			async set(newV) {
				if ((newV && this.isVisible) || (!newV && !this.isVisible)) {
					return;
				}
				this.loadingVisibilityChange = true;
				try {
					await this.toggleWorkflowIsOnSettings({ id: this.workflowId, userId: this.userId });
				} catch (e) {
					this.$toast.error('Error updating user visibility, please try again later.');
				} finally {
					this.loadingVisibilityChange = false;
				}
			},
		},
		userEmail() {
			return this.user.email;
		},
		isWorkflowOwner() {
			return this.workflow.owner === this.userId;
		},
		userFullName() {
			return AppAvatarService.getUserFullName(this.user) || this.userEmail;
		},
	},
	methods: {
		...mapActionsShorthand({ updateWorkflowUserRole, toggleWorkflowIsOnSettings }),
		async updateUserVisibility(userEmails, role) {
			this.loadingVisibilityChange = true;
			try {
				await this.updateWorkflowUserRole({ flowId: this.workflowId, userEmails, role });
			} catch (e) {
				this.$toast.error('Error updating user visibility, please try again later.');
			} finally {
				this.loadingVisibilityChange = false;
			}
		},
		removeAccess() {
			if (this.allowRemoveAccess) {
				this.updateUserVisibility([this.user.email], null);
			}
		},
		setVisibleLocked() {
			this.isLocked = true;
			this.isVisible = true;
		},
		setHidden() {
			this.isLocked = false;
			this.isVisible = false;
		},
		setVisible() {
			this.isLocked = false;
			this.isVisible = true;
		},
	},
	data() {
		return {
			loadingVisibilityChange: false,
		};
	},
};
</script>

<style scoped lang="scss">
.shared-user-container {
	padding: 5px 0px;
	transition: var(--transition-time);
	&:hover {
		background: #f6f8fb;
		transition: var(--transition-time);
		.show-on-hover {
			display: flex;
		}
		::v-deep(.visibility-dropdown) {
			.activator-container {
				transition: var(--transition-time);
				background: #f6f8fb;
			}
		}
	}
}
.setup-required-text {
	opacity: 0.5;
	color: var(--bold-title);
}
.show-on-hover {
	display: none;
}
.visibility-state-text {
	@extend .setup-required-text;
}
::v-deep(.visibility-dropdown) {
	.activator-container {
		border: unset;
		transition: var(--transition-time);
	}
}
.user-email-text {
	@extend .setup-required-text;
}
</style>
